import { createReducer, on } from "@ngrx/store";
import { completeMeal, completeMealFailure, completeMealSuccess, deletePlannedMeal, deletePlannedMealFailure, deletePlannedMealSuccess, generateMealSuggestions, generateMealSuggestionsFailure, generateMealSuggestionsSuccess, loadMealPlannerQuota, loadMealPlannerQuotaFailure, loadMealPlannerQuotaSuccess, loadOngoingMeals, loadOngoingMealsFailure, loadOngoingMealsSuccess, loadPlannedMeals, loadPlannedMealsFailure, loadPlannedMealsQuota, loadPlannedMealsQuotaFailure, loadPlannedMealsQuotaSuccess, loadPlannedMealsSuccess, resetMealPlanner, startMeal, startMealFailure, startMealSuccess, upsertPlannedMeal, upsertPlannedMealFailure, upsertPlannedMealSuccess, updatePlannedMealStatus, updatePlannedMealStatusSuccess, updatePlannedMealStatusFailure } from "./meal-planner.actions";
import { OngoingMeal } from "../../../shared/models/ongoing-meal.model";
import { QuotaInfo } from "../../../shared/models/quota-info.model";
import { SuggestedMealsByType } from "../../../shared/models/meal-planner.model";
import { PlannedMealWithRecipe } from "../../../shared/models/planned-meal.model";

const getDateKey = (value: string | Date | null | undefined): string | null => {
  if (!value) return null;
  if (typeof value === 'string') {
    return value.length >= 10 ? value.substring(0, 10) : null;
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().substring(0, 10);
  }
  return null;
};

export interface MealPlannerState {
  ongoingMeals: OngoingMeal[];
  loading: boolean;
  generationLoading: boolean;
  generationError: any;
  error: any;
  quota: QuotaInfo | null;
  suggestions: SuggestedMealsByType[];
  hasStorageItems: boolean;
  isMealPlannerQuotaLoading: boolean;
  // Planned meals
  plannedMeals: PlannedMealWithRecipe[];
  plannedMealsLoading: boolean;
  plannedMealsError: any;
  plannedMealsFromDate: string | null;
  plannedMealsToDate: string | null;
  plannedMealsQuota: QuotaInfo | null;
  plannedMealsQuotaLoading: boolean;
}

export const initialState: MealPlannerState = {
  ongoingMeals: [],
  loading: false,
  generationLoading: false,
  generationError: null,
  error: null,
  quota: null,
  suggestions: [],
  hasStorageItems: true,
  isMealPlannerQuotaLoading: false,
  // Planned meals
  plannedMeals: [],
  plannedMealsLoading: false,
  plannedMealsError: null,
  plannedMealsFromDate: null,
  plannedMealsToDate: null,
  plannedMealsQuota: null,
  plannedMealsQuotaLoading: false
};

export const mealPlannerReducer = createReducer(
  initialState,
  
  // Meal generation
  on(generateMealSuggestions, (state) => ({
    ...state,
    generationLoading: true,
    error: null
  })),
  on(generateMealSuggestionsSuccess, (state, { suggestions, hasStorageItems }) => ({
  ...state,
  generationLoading: false,
  suggestions,
  hasStorageItems
})),
  on(generateMealSuggestionsFailure, (state, { error, hasStorageItems }) => ({
    ...state,
    generationLoading: false,
    suggestions: [],
    hasStorageItems,
    generationError: error
  })),

  // Start meal
  on(startMeal, state => ({ ...state, loading: true })),
  on(startMealSuccess, (state, { meal }) => ({
    ...state,
    loading: false,
    ongoingMeals: [...state.ongoingMeals, meal]
  })),
  on(startMealFailure, (state, { error }) => ({ ...state, loading: false, error })),

  on(loadMealPlannerQuota, state => ({ ...state, isMealPlannerQuotaLoading: true })),
  on(loadMealPlannerQuotaSuccess, (state, { quota }) => ({
        ...state,
        quota,
        isMealPlannerQuotaLoading: false
      })),
  on(loadMealPlannerQuotaFailure, (state) => ({
        ...state,
        quota: null,
        isMealPlannerQuotaLoading: false
  }   )),

  // Load ongoing meals
  on(loadOngoingMeals, state => ({ ...state, loading: true })),
  on(loadOngoingMealsSuccess, (state, { meals }) => ({
    ...state,
    loading: false,
    ongoingMeals: meals
  })),
  on(loadOngoingMealsFailure, (state, { error }) => ({ ...state, loading: false, error })),

  // Complete meal
  on(completeMeal, state => ({ ...state, loading: true })),
  on(completeMealSuccess, (state, { id }) => ({
    ...state,
    loading: false,
    ongoingMeals: state.ongoingMeals.filter(meal => meal.id !== id)
  })),
  on(completeMealFailure, (state, { error }) => ({ ...state, loading: false, error })),

  // Load planned meals
  on(loadPlannedMeals, (state, { fromDate, toDate }) => ({
    ...state,
    plannedMealsLoading: true,
    plannedMealsError: null,
    plannedMealsFromDate: fromDate,
    plannedMealsToDate: toDate
  })),
  on(loadPlannedMealsSuccess, (state, { plannedMeals }) => ({
    ...state,
    plannedMealsLoading: false,
    plannedMeals
  })),
  on(loadPlannedMealsFailure, (state, { error }) => ({
    ...state,
    plannedMealsLoading: false,
    plannedMealsError: error
  })),

  // Delete planned meal
  on(deletePlannedMeal, state => ({ ...state, plannedMealsLoading: true })),
  on(deletePlannedMealSuccess, (state, { plan_date, slot_type, planned_meal_id }) => ({
    ...state,
    plannedMealsLoading: false,
    plannedMeals: state.plannedMeals.filter(pm => {
      if (planned_meal_id != null) {
        return pm.planned_meal_id !== planned_meal_id;
      }

      const pmDateStr = getDateKey(pm.plan_date);
      return !(pmDateStr === plan_date && pm.slot_type === slot_type);
    }),
    plannedMealsQuota: state.plannedMealsQuota 
      ? { ...state.plannedMealsQuota, used: Math.max(0, state.plannedMealsQuota.used - 1) }
      : null
  })),
  on(deletePlannedMealFailure, (state, { error }) => ({
    ...state,
    plannedMealsLoading: false,
    plannedMealsError: error
  })),

  // Upsert planned meal
  on(upsertPlannedMeal, state => ({ ...state, plannedMealsLoading: true })),
  on(upsertPlannedMealSuccess, (state, { plannedMeal }) => {
    const newMealDateStr = getDateKey(plannedMeal.plan_date);
    
    const existingIndex = state.plannedMeals.findIndex(pm => {
      const pmDateStr = getDateKey(pm.plan_date);
      return pmDateStr === newMealDateStr && pm.slot_type === plannedMeal.slot_type;
    });
    
    const updatedMeals = existingIndex >= 0
      ? state.plannedMeals.map((pm, idx) => idx === existingIndex ? plannedMeal : pm)
      : [...state.plannedMeals, plannedMeal];

    // Update quota: increment used count only if it's a new meal (not a replacement)
    const updatedQuota = state.plannedMealsQuota && existingIndex < 0
      ? { ...state.plannedMealsQuota, used: state.plannedMealsQuota.used + 1 }
      : state.plannedMealsQuota;

    return {
      ...state,
      plannedMealsLoading: false,
      plannedMeals: updatedMeals,
      plannedMealsQuota: updatedQuota
    };
  }),
  on(upsertPlannedMealFailure, (state, { error }) => ({
    ...state,
    plannedMealsLoading: false,
    plannedMealsError: error
  })),

  // Load planned meals quota
  on(loadPlannedMealsQuota, state => ({ ...state, plannedMealsQuotaLoading: true })),
  on(loadPlannedMealsQuotaSuccess, (state, { quota }) => ({
    ...state,
    plannedMealsQuota: quota,
    plannedMealsQuotaLoading: false
  })),
  on(loadPlannedMealsQuotaFailure, state => ({
    ...state,
    plannedMealsQuota: null,
    plannedMealsQuotaLoading: false
  })),

  on(resetMealPlanner, () => initialState),

  // Update planned meal status (PLANNED→STARTED or STARTED→DONE)
  on(updatePlannedMealStatus, state => ({ ...state, plannedMealsLoading: true })),
  on(updatePlannedMealStatusSuccess, (state, { plannedMeal }) => ({
    ...state,
    plannedMealsLoading: false,
    plannedMeals: state.plannedMeals.map(pm =>
      pm.planned_meal_id === plannedMeal.planned_meal_id ? plannedMeal : pm
    )
  })),
  on(updatePlannedMealStatusFailure, (state, { error }) => ({
    ...state,
    plannedMealsLoading: false,
    plannedMealsError: error
  })),
);
